// A post's labels (D-100): the brand's own, plus the AI's suggestions to confirm or turn down,
// and adding one (or a new one). Used in the Inbox, Discover and the wall editor's post panel.
import type { ActionResult, Label, Post } from "@brandwall/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router";
import { ApiError, api, post } from "../api";
import { useToast } from "./Toast";

export function useLabels() {
  const { ws } = useParams();
  return useQuery({
    queryKey: ["labels", ws],
    queryFn: () =>
      api<{ labels: Label[]; rules: { id: string; hashtag: string; labelId: string }[] }>(
        `/v1/w/${ws}/labels`,
      ),
  });
}

/** Adds or takes off labels on posts, making a new label first when asked to. */
export function useLabelPosts() {
  const { ws } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  return async (ids: string[], change: { add?: string[]; remove?: string[]; newName?: string }) => {
    try {
      let add = change.add ?? [];
      if (change.newName?.trim()) {
        const made = await post<ActionResult>(`/v1/w/${ws}/labels`, {
          name: change.newName.trim(),
        });
        add = [...add, ...made.ids];
      }
      const r = await post<ActionResult>(`/v1/w/${ws}/posts/labels`, {
        ids,
        add,
        remove: change.remove ?? [],
      });
      toast(r.summary);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["posts", ws] }),
        qc.invalidateQueries({ queryKey: ["labels", ws] }),
        qc.invalidateQueries({ queryKey: ["discover", ws] }),
        qc.invalidateQueries({ queryKey: ["wall-preview", ws] }),
      ]);
      return true;
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "That didn't work. Please try again.");
      return false;
    }
  };
}

/** Choose a label to add: one of the brand's, or a new one. */
export function LabelPicker({
  onPick,
  exclude = [],
  label = "Add a label",
}: {
  onPick: (pick: { id?: string; newName?: string }) => void;
  exclude?: string[];
  label?: string;
}) {
  const labels = useLabels();
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  if (naming)
    return (
      <form
        className="row"
        style={{ gap: 6 }}
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) onPick({ newName: name.trim() });
          setName("");
          setNaming(false);
        }}
      >
        <input
          className="input"
          style={{ width: 150 }}
          maxLength={40}
          aria-label="New label"
          placeholder="e.g. Women, Summer"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button type="submit" className="btn sm" disabled={!name.trim()}>
          Add
        </button>
      </form>
    );
  return (
    <select
      className="input"
      style={{ width: "auto", fontSize: 13, padding: "2px 6px" }}
      aria-label={label}
      value=""
      onChange={(e) => {
        if (e.target.value === "__new") setNaming(true);
        else if (e.target.value) onPick({ id: e.target.value });
      }}
    >
      <option value="">{label}…</option>
      {(labels.data?.labels ?? [])
        .filter((l) => !exclude.includes(l.id))
        .map((l) => (
          <option key={l.id} value={l.id}>
            {l.name}
          </option>
        ))}
      <option value="__new">New label…</option>
    </select>
  );
}

export function PostLabels({ p, canAct }: { p: Pick<Post, "id" | "labels">; canAct: boolean }) {
  const labelPosts = useLabelPosts();
  const confirmed = p.labels.filter((l) => l.status === "confirmed");
  const suggested = p.labels.filter((l) => l.status === "suggested");
  if (!canAct && p.labels.length === 0) return null;
  return (
    <div className="row wrap" style={{ gap: 6, alignItems: "center" }}>
      {confirmed.map((l) => (
        <span key={l.id} className="pill">
          {l.name}
          {canAct && (
            <button
              type="button"
              className="btn link small"
              aria-label={`Take the label ${l.name} off`}
              style={{ marginLeft: 4, padding: 0 }}
              onClick={() => void labelPosts([p.id], { remove: [l.id] })}
            >
              ×
            </button>
          )}
        </span>
      ))}
      {suggested.map((l) => (
        <span key={l.id} className="pill warn" title="Suggested by Brandwall's AI">
          {l.name}?
          {canAct && (
            <>
              <button
                type="button"
                className="btn link small"
                aria-label={`Confirm the label ${l.name}`}
                style={{ marginLeft: 4, padding: 0 }}
                onClick={() => void labelPosts([p.id], { add: [l.id] })}
              >
                ✓
              </button>
              <button
                type="button"
                className="btn link small"
                aria-label={`Turn down the label ${l.name}`}
                style={{ marginLeft: 2, padding: 0 }}
                onClick={() => void labelPosts([p.id], { remove: [l.id] })}
              >
                ×
              </button>
            </>
          )}
        </span>
      ))}
      {canAct && (
        <LabelPicker
          exclude={confirmed.map((l) => l.id)}
          onPick={(pick) =>
            void labelPosts([p.id], pick.id ? { add: [pick.id] } : { newName: pick.newName })
          }
        />
      )}
    </div>
  );
}
